import React, { useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { api } from "../../lib/api";
import { formatInrLoose } from "../../lib/currency";
import { useAuth } from "../../providers/AuthProvider";
import { TableLayout, type TableColumn } from "../../components/dashboard";
import { Alert, AlertDescription, AlertTitle } from "../../app/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../app/components/ui/tabs";
import { ReportsDateRangeFilterBar } from "../../app/components/reports/ReportsDateRangeFilterBar";

type PlatformOverviewData = {
  total_businesses: number;
  verified_businesses: number;
  pending_businesses: number;
  total_sales: string;
  total_orders: number;
};

type BusinessPerformanceRow = {
  business_id: string;
  business_name: string;
  total_sales: string;
  total_orders: number;
  last_activity: string | null;
};

function parseMoney(v: string) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function ReportsHubPage() {
  const { token } = useAuth();

  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);

  const [overview, setOverview] = useState<PlatformOverviewData | null>(null);
  const [businessRows, setBusinessRows] = useState<BusinessPerformanceRow[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      try {
        const [ovRes, bpRes] = await Promise.all([
          api.adminReports.getPlatformOverview({
            token,
            filters: { from_date: fromDate, to_date: toDate },
          }),
          api.adminReports.getBusinessPerformance({
            token,
            filters: { from_date: fromDate, to_date: toDate },
          }),
        ]);

        if (cancelled) return;

        setOverview(ovRes.data as PlatformOverviewData);
        setBusinessRows((bpRes.data as BusinessPerformanceRow[]) ?? []);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : "Failed to load admin reports");
        setOverview(null);
        setBusinessRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [token, fromDate, toDate]);

  const topBusinessesForChart = useMemo(() => {
    return businessRows.slice(0, 6).map((r) => ({
      name: r.business_name,
      total_sales: parseMoney(r.total_sales),
    }));
  }, [businessRows]);

  const businessPerfColumns: TableColumn<BusinessPerformanceRow>[] = useMemo(
    () => [
      {
        key: "business",
        header: "Business",
        render: (r) => (
          <div className="min-w-0 leading-tight">
            <div className="font-medium">{r.business_name}</div>
            <div className="font-mono text-xs text-muted-foreground">{r.business_id}</div>
          </div>
        ),
      },
      {
        key: "orders",
        header: "Orders",
        render: (r) => <span className="block text-right tabular-nums">{r.total_orders}</span>,
      },
      {
        key: "sales",
        header: "Total sales",
        render: (r) => <span className="block text-right tabular-nums">{r.total_sales}</span>,
      },
      {
        key: "last",
        header: "Last activity",
        render: (r) => (
          <span className="block text-right text-sm">
            {r.last_activity ? new Date(r.last_activity).toLocaleString() : "-"}
          </span>
        ),
      },
    ],
    [],
  );

  const platformTotalSales = overview?.total_sales ?? "0.00";

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-start md:justify-between">
        <div>
          <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Reports</h1>
          <p className="text-muted-foreground">Platform overview and business performance.</p>
        </div>
        <ReportsDateRangeFilterBar
          fromDate={fromDate}
          toDate={toDate}
          onChange={({ fromDate: nextFrom, toDate: nextTo }) => {
            setFromDate(nextFrom);
            setToDate(nextTo);
          }}
        />
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Platform overview</TabsTrigger>
          <TabsTrigger value="business">Business performance</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total businesses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">{overview?.total_businesses ?? (loading ? "..." : 0)}</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Verified</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  {overview?.verified_businesses ?? (loading ? "..." : 0)}
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Pending</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  {overview?.pending_businesses ?? (loading ? "..." : 0)}
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm md:col-span-2 lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total sales</CardTitle>
                <CardDescription>Based on sales totals in the selected range.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">{platformTotalSales}</div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Total orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-semibold">
                  {overview?.total_orders ?? (loading ? "..." : 0)}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-sm mt-4">
            <CardHeader>
              <div>
                <CardTitle>Top businesses by sales</CardTitle>
                <CardDescription>Shows up to 6 businesses for the selected date range.</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-muted-foreground">Loading chart...</div>
              ) : topBusinessesForChart.length === 0 ? (
                <div className="text-muted-foreground">No sales data in the selected range.</div>
              ) : (
                <div className="h-[260px] min-h-[220px] w-full min-w-0 md:h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topBusinessesForChart}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tickLine={false} axisLine={false} />
                      <YAxis tickLine={false} axisLine={false} />
                      <Tooltip
                        formatter={(value: number) => [formatInrLoose(value), "Sales"]}
                        labelFormatter={(label) => label}
                      />
                      <Bar dataKey="total_sales" fill="#2563eb" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="business">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <div>
                <CardTitle>Business performance</CardTitle>
                <CardDescription>Total sales and orders per business.</CardDescription>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-muted-foreground">Loading table...</div>
              ) : businessRows.length === 0 ? (
                <div className="text-muted-foreground">No businesses found for the selected range.</div>
              ) : (
                <div className="min-w-0 rounded-md border md:border-0">
                  <TableLayout columns={businessPerfColumns} rows={businessRows} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

